import React from "react";
import { Breadcrumb } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./CustomBreadcrumb.css";
import { RootState } from "../../store";

// AuthReducer initializes userType to this value before the real one is
// known (see AuthReducer.ts). If Redux still has the placeholder — e.g.
// right after a page refresh, before the slice rehydrates — fall back to
// the value persisted in localStorage instead of misreading it as "unset".
const USER_TYPE_NOT_LOADED = 565;

// Matches AppRoutes.tsx's own userType === 1 check for routing to
// homeRoutesClient — this is the one and only userType that means "client".
// Anything else (0, or any other value) is treated as non-client.
const CLIENT_USER_TYPE = 1;

const resolveUserType = (reduxUserType: number): number => {
  if (reduxUserType !== USER_TYPE_NOT_LOADED) return reduxUserType;

  const savedUserType = localStorage.getItem("userType");
  return savedUserType ? parseInt(savedUserType, 10) : USER_TYPE_NOT_LOADED;
};

// "Business Clients ▸ <current page>" for everyone else, "Processing"
// for the current page whenever a client is logged in — client accounts
// don't have a Business Clients landing page, so pointing at it would be
// a dead link for them.
const resolveCurrentPage = (isClientUser: boolean, pathSegments: string[]) => {
  if (isClientUser) return "processing";
  return pathSegments[0] ?? "Business Clients";
};

const formatPageTitle = (page: string): string =>
  page
    .replace(/-/g, " ") // "to-review" -> "to review"
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const CustomBreadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const reduxUserType = useSelector(
    (state: RootState) => state.auth.userType,
  );
  const userType = resolveUserType(reduxUserType);
  const isClientUser = userType === CLIENT_USER_TYPE;

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const currentPage = resolveCurrentPage(isClientUser, pathSegments);
  const formattedCurrentPage = formatPageTitle(currentPage);

  const onBreadcrumbClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="CustomBreadcrumb">
      <Breadcrumb>
        <Breadcrumb.Item key="home">
          <div
            className="CustomBreadcrumb_path"
            onClick={() => onBreadcrumbClick("/")}
          >
            Home
          </div>
        </Breadcrumb.Item>

        {pathSegments.map((segment, index) => {
          const path = "/" + pathSegments.slice(0, index + 1).join("/");
          const isLast = index === pathSegments.length - 1;

          return (
            <Breadcrumb.Item
              key={path}
              className={isLast ? "CustomBreadcrumb_path_current" : ""}
            >
              {isLast ? (
                formatPageTitle(segment)
              ) : (
                <div
                  className="CustomBreadcrumb_path"
                  onClick={() => onBreadcrumbClick(path)}
                >
                  {formatPageTitle(segment)}
                </div>
              )}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
    </div>
  );
};
export default CustomBreadcrumb;
